import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAppStore } from '../store';
import type { Task } from '../types';
import { useEffect, useRef, useCallback } from 'react';

export function useRecurrenceGenerator() {
  const queryClient = useQueryClient();
  const spaceId = useAppStore((s) => s.currentSpaceId);
  const hasGenerated = useRef(false);

  useEffect(() => {
    if (hasGenerated.current) return;
    hasGenerated.current = true;

    api.recurring.generate(spaceId).then(() => {
      queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
    });
  }, [spaceId, queryClient]);

  const regenerate = useCallback(async () => {
    await api.recurring.generate(spaceId);
    queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
  }, [spaceId, queryClient]);

  return { regenerate };
}

export function useRecurringTemplates() {
  const spaceId = useAppStore((s) => s.currentSpaceId);
  const { data } = useQuery({
    queryKey: ['recurring-templates', spaceId],
    queryFn: () => api.recurring.templates(spaceId),
  });
  return (data ?? []) as Task[];
}

export function usePauseTemplate() {
  const queryClient = useQueryClient();
  const spaceId = useAppStore((s) => s.currentSpaceId);
  return useMutation({
    mutationFn: (id: string) => api.recurring.pause(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
      queryClient.invalidateQueries({ queryKey: ['recurring-templates'] });
    },
  });
}

export function useResumeTemplate() {
  const queryClient = useQueryClient();
  const spaceId = useAppStore((s) => s.currentSpaceId);
  return useMutation({
    mutationFn: (id: string) => api.recurring.resume(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
      queryClient.invalidateQueries({ queryKey: ['recurring-templates'] });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  const spaceId = useAppStore((s) => s.currentSpaceId);
  return useMutation({
    mutationFn: (id: string) => api.recurring.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
      queryClient.invalidateQueries({ queryKey: ['recurring-templates'] });
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  const spaceId = useAppStore((s) => s.currentSpaceId);
  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Record<string, unknown> }) =>
      api.recurring.update(id, changes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', spaceId, 'track'] });
      queryClient.invalidateQueries({ queryKey: ['recurring-templates'] });
    },
  });
}
